import { useEffect, useState, useRef, useCallback, useContext, useMemo } from 'react'
import ReactPlayer from 'react-player'

import EasyDirectoryLogo from '../../assets/brand/TCF_EasyDirectory_Logo_RGB.png'
import LicenseImg from '../../assets/images/Step1_BuyLicense.jpg'
import SupportImg from '../../assets/images/Step4_Support.jpg'

import Select from 'react-select'

import { v4 as uuid } from 'uuid'

import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CButton,
  CCardTitle,
  CCardText,
  CCardImage,
  CFormSwitch,
  CFormInput,
  CSpinner,
  CImage,
  CLink,
  CToast,
  CToastBody,
  CToastClose,
  CToaster,
  CAlert,
  CContainer
} from '@coreui/react'
import CIcon from '@coreui/icons-react';
import {
  cilPhone,
  cilLoopCircular,
  cilX,
  cilTrash,
  cilGroup,
} from '@coreui/icons'

import "./EasyDirectory.css"
import { arraysAreEqual } from '@microsoft/mgt-element'
import { useTranslation } from 'react-i18next'
import { Seat, EasyDirectoryConfiguration, EasyTag, EasyDirectoryFeatures, ViewSelect, View, SharedMailbox, ViewTypes, ViewPermissions } from '../../common/interfaces'
import RenderRoleManager from '../../components/EasyDirectory/RoleManager'
import ConfirmationModal from '../../components/modal/ConfirmationModal'
import { ConfigurationService } from '../../services/ConfigurationService'
import { GraphService } from '../../services/GraphService'
import { SubscriptionService } from '../../services/SubscriptionService'
import { EasyContext } from '../../components/context/EasyContext'
import { countSeats } from '../../components/helper/Helper'

interface EmailValidationProps {
  configuration: EasyDirectoryConfiguration;
  setConfiguration: (args: EasyDirectoryConfiguration) => void;
  setEnableSaveSettings: (args: boolean) => void;
  easyDirectorySource: SharedMailbox[];
  setEasyDirectorySource;
}

const EasyDirectory = () => {
  const { t } = useTranslation();
  const funMessage = "Loading...";
  const easyContext = useContext(EasyContext);

  const subscriptionService = new SubscriptionService(easyContext.accessToken);
  const configurationService = new ConfigurationService(easyContext.accessToken);
  const graphService = new GraphService(easyContext.accessToken, false);

  const [seatsCount, setSeatsCount] = useState<number>(0);

  const [configuration, setConfiguration] = useState<EasyDirectoryConfiguration>({});
  const [pristineOrgTags, setPristineOrgTags] = useState<EasyTag[]>([]);
  const [pristineOrgViews, setPristineOrgViews] = useState<View[]>([]);
  const [pristineSettings, setPristineSettings] = useState<EasyDirectoryFeatures>({});

  const [enableSaveSettings, setEnableSaveSettings] = useState(false);
  const [enableSaveTags, setEnableSaveTags] = useState(false);
  const [enableSaveViews, setEnableSaveViews] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [customViews, setOrganisationViews] = useState<ViewSelect[]>();
  const [viewSelectionList, setViewSelectionList] = useState([]);
  const [selectedView, setSelectedView] = useState<View>(null);
  const [easyDirectorySource, setEasyDirectorySource] = useState([{ address: "", id: "", label: "" }]);
  const [isPageConfigLoading, setIsPageConfigLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState(t("SettingsSaved"));
  const [loadingMsg, setLoadingMsg] = useState(funMessage);
  const [isLoading, setIsLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const toggleModal = () => setModalVisible(!modalVisible);
  const [onConfirm, setOnConfirm] = useState(() => () => { return }); // Default no-op function

  const [toast, addToast] = useState<any>();
  const toaster = useRef();

  const getConfiguration = useCallback(() => {
    const attemptConfiguration = async () => {
      if (easyContext.accessToken) {
        setIsPageConfigLoading(true);
        setLoadingMsg(t("Loading"));

        try {
          const config = await configurationService.GetEasyDirectoryConfiguration(easyContext.accessToken);
          setConfiguration(config);
          const settings: EasyDirectoryFeatures = {
            enablePersonalMailboxSearch: config?.enablePersonalMailboxSearch,
            enableEntraIdSearch: config?.enableEntraIdSearch,
            enableSharedMailboxSearch: config?.enableSharedMailboxSearch,
            id: config?.id,
            sharedMailboxes: config?.sharedMailboxes
          };
          setPristineSettings(settings);
          setPristineOrgTags([...config.orgTags]);
          setPristineOrgViews([...(config.orgViews || [])]);
          setEasyDirectorySource(config.sharedMailboxes);
          setIsPageConfigLoading(false);
          getOrganisationViews();
        } catch (e) {
          setIsPageConfigLoading(false);
        }
      } else {
        setErrorMsg(t("EasyDirectory.MigrationWizard.Common.Retry"));
      }
    }

    attemptConfiguration();
  }, [easyContext.accessToken]);


  const getOrganisationViews = useCallback(() => {
    const convertToOptions = (views: View[]) => {
      const convertedViews = [];
      views.forEach(view => {
        convertedViews.push({
          value: view?.id,
          label: view?.label,
          mode: "View"
        });
      });
      return convertedViews;
    };

    configurationService.GetOrganisationViews().then(async (result) => {
      const views = [];
      const userOptions = [];

      for (const userConfig of result) {
        const notAddedViews = userConfig.views?.filter(v => !configuration?.orgViews?.some(orgView => orgView.id === v.id));

        if (notAddedViews.length > 0) {
          try {
            const person = await graphService.getUser(userConfig.userId);
            if (person) {
              const username = person.displayName;

              views.push({
                label: username,
                options: convertToOptions(notAddedViews)
              });

              userOptions.push({
                value: userConfig.userId,
                label: username
              });
            }
          } catch (error) {
            const err = error as Error;
            setErrorMsg(err.message);
          }

        }
      }
      setViewSelectionList(views);
      setOrganisationViews(result);
    }).catch((e) => {
      const err = e as Error;
      setErrorMsg(err.message);
    });
  }, [graphService, configuration]);

  const getSeats = async (subId: string): Promise<number> => await subscriptionService.getEasyDirectorySeats(subId).then((seats: Seat[]) => {
    return countSeats(seats);
  }).catch((e) => {
    setErrorMsg("Seats can not be loaded: " + e.message);
    return 0;
  });

  useEffect(() => {
    easyContext?.subscriptions?.map(async sub => {
      if (sub.state === 3 && sub.offer_id.toLowerCase().includes('easydirectory')) {
        const loadedSeats = await getSeats(sub.subscription_id);
        setSeatsCount(loadedSeats);

      }
    });
  }, [easyContext?.subscriptions]);


  useMemo(() => {
    if (easyContext.accessToken) {
      getConfiguration();
    }
  }, [easyContext.accessToken]);

  useEffect(() => {
    if (errorMsg) {
      addToast(errorToast);
    }
  }, [errorMsg]);

  useEffect(() => {
    if (configuration && pristineOrgTags && pristineOrgViews && pristineSettings) {
      setEnableSaveTags(!arraysAreEqual(pristineOrgTags, configuration.orgTags));
      setEnableSaveViews(!arraysAreEqual(pristineOrgViews, configuration.orgViews));
      setEnableSaveSettings(!arraysAreEqual(pristineSettings.sharedMailboxes, configuration.sharedMailboxes)
        || pristineSettings.enableEntraIdSearch !== configuration.enableEntraIdSearch
        || pristineSettings.enablePersonalMailboxSearch !== configuration.enablePersonalMailboxSearch
        || pristineSettings.enableSharedMailboxSearch !== configuration.enableSharedMailboxSearch
      );
    }
  }, [configuration, pristineOrgTags, pristineOrgViews, pristineSettings]);
  
  const saveSettings = async () => {
    setIsLoading(true);
    const settings: EasyDirectoryFeatures = {
      enablePersonalMailboxSearch: configuration.enablePersonalMailboxSearch,
      enableEntraIdSearch: configuration.enableEntraIdSearch,
      enableSharedMailboxSearch: true, // True by default
      id: configuration?.id,
      sharedMailboxes: configuration?.sharedMailboxes
    };

    try {

      // Entra ID Import/Delete
      if (configuration.enableEntraIdSearch !== pristineSettings.enableEntraIdSearch) {
        if (configuration.enableEntraIdSearch) {
          await configurationService.ImportEntraId(configuration);
        } else {
          await configurationService.DeleteEntraIdContacts();
        }
      }

      // Shared Mailboxes Import/Delete
      if (configuration.sharedMailboxes && pristineSettings.sharedMailboxes) {
        const currentMailboxes = new Set(configuration.sharedMailboxes.map(m => m.address));
        const previousMailboxes = new Set(pristineSettings.sharedMailboxes.map(m => m.address));

        // Handle new mailboxes
        for (const mailbox of configuration.sharedMailboxes) {
          if (!previousMailboxes.has(mailbox.address)) {
            await configurationService.ImportSharedMailboxes(mailbox.address);
          }
        }

        // Handle deleted mailboxes
        for (const mailbox of pristineSettings.sharedMailboxes) {
          if (!currentMailboxes.has(mailbox.address)) {
            await configurationService.DeleteSharedMailbox(mailbox.address);
          }
        }
      }

      await configurationService.UpdateEDFeatures(settings);
      setPristineSettings({ ...settings } as EasyDirectoryFeatures);
      setSuccessMsg(t("SettingsSaved"));
      addToast(successfulToast);

    } catch (e) {
      console.log(e);
      addToast(errorToast);
    } finally {
      setIsLoading(false);
    }
  };

  const saveTags = () => {
    const updateConfig = async () => {
      await configurationService.UpdateOrgTags(configuration?.id, configuration?.orgTags)
        .then(() => {
          setPristineOrgTags([...configuration.orgTags]);
          setSuccessMsg(t("SettingsSaved"));
          addToast(successfulToast);
        }).catch((e) => {
          setErrorMsg(e.message);
        });
    };
    updateConfig();
  };

  const saveView = () => {
    const updateConfig = async () => {
      await configurationService.UpdateOrgViews(configuration?.id, configuration?.orgViews)
        .then(() => {
          setPristineOrgViews([...configuration.orgViews]);
          setSuccessMsg(t("SettingsSaved"));
          addToast(successfulToast);
        }).catch((e) => {
          setErrorMsg(e.message);
        });
    };
    updateConfig();
  };

  const openModal = (action: () => void) => {
    setOnConfirm(() => action);
    toggleModal();
  };

  const successfulToast = (
    <CToast autohide={true} visible={true} color="success" role="img" className="text-white align-items-center" onClose={() => { setSuccessMsg("") }}>
      <div className="d-flex">
        <CToastBody>{successMsg}</CToastBody>
        <CToastClose className="me-2 m-auto" white />
      </div>
    </CToast>
  )

  const errorToast = (
    <CToast autohide={true} visible={true} color="danger" role="img" className="text-white align-items-center" onClose={() => { setErrorMsg("Reset") }}>
      <div className="d-flex">
        <CToastBody>{errorMsg}</CToastBody>
        <CToastClose className="me-2 m-auto" white />
      </div>
    </CToast>
  )

  const updateConfiguration = (updates: Partial<EasyDirectoryConfiguration>): void => {
    setConfiguration({ ...configuration, ...updates });
  }

  const RenderEmailValidation = (props: EmailValidationProps) => {
    const { configuration, setConfiguration, easyDirectorySource, setEasyDirectorySource, setEnableSaveSettings } = props;
    const [emailInvalid, setEmailInvalid] = useState(false);
    const [hasEmailTwice, setHasEmailTwice] = useState(false);
    const [errorFields, setErrorFields] = useState<number[]>([]);

    useEffect(() => {
      validateEmails();
    }, []);

    const saveEDSource = (source, index) => {
      setEasyDirectorySource((prevEDSource) => {
        const newEDSource = [...prevEDSource];
        newEDSource[index].address = source;
        newEDSource[index].label = source;
        return newEDSource;
      });
    };

    const validateEmails = () => {
      if (easyDirectorySource[easyDirectorySource?.length - 1]?.address !== "") {
        const newMailbox: SharedMailbox = { address: "", id: "", label: "" };
        setEasyDirectorySource(easyDirectorySource.concat(newMailbox));
      }
      easyDirectorySource?.map(((email, index) => {
        validateEmail(email.address, index);
      }));
    }

    const checkEmailAlreadyExists = (email: string) => {
      return easyDirectorySource.some(c => c.address === email) ?? false;
    }

    const checkEmailExistsInGraph = (email: string) => {
      return graphService.getUser(email).then(() => {
        return true;
      }).catch(() => {
        return false;
      })
    }

    const validateEmail = async (email, index) => {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emailExists = await checkEmailExistsInGraph(email);

      if ((index !== (easyDirectorySource?.length - 1) && email === "" && emailExists)) {
        const newSource = [...easyDirectorySource];
        newSource.splice(index, 1);

        setEasyDirectorySource(newSource);

        errorFields.indexOf(index) > -1 && errorFields.splice(errorFields.indexOf(index), 1);

        const config = { ...configuration, sharedMailboxes: newSource.slice(0, -1) };

        setConfiguration(config);
        setEmailInvalid(false);
        setHasEmailTwice(false);
      } else if ((!regex.test(email) || (new Set(easyDirectorySource)).size !== easyDirectorySource?.length || !emailExists)) {
        if (email !== "") {
          if ((new Set(easyDirectorySource)).size !== easyDirectorySource?.length) {
            setHasEmailTwice(true);
          } else {
            setEmailInvalid(true);
          }
          setErrorFields([...errorFields, index]);
          setEnableSaveSettings(false);
        }
      }
    };

    const handleSharedMailbox = (e, index) => {
      const sharedMailbox = (e.target as HTMLInputElement).value;

      const emailExists = checkEmailAlreadyExists(sharedMailbox);

      if (!emailExists && easyDirectorySource.length < 6) {
        saveEDSource(sharedMailbox, index)
        const newSource = [...easyDirectorySource];

        newSource[index]!.address = sharedMailbox;
        newSource[index]!.label = sharedMailbox;

        const config = { ...configuration, sharedMailboxes: newSource };

        if (config?.sharedMailboxes[config?.sharedMailboxes?.length - 1]?.address === "") {
          config.sharedMailboxes.splice(-1);
        }

        setConfiguration(config);
      }
      else {
        setErrorFields([...errorFields, index]);
      }
    }

    return (
      <div>
        {(emailInvalid) && <CAlert color="danger">
          {t("Onboarding.ErrorEmail")}
        </CAlert>}
        {(hasEmailTwice) && <CAlert color="danger">
          {t("Onboarding.ErrorEmailTwice")}
        </CAlert>}
        {easyDirectorySource?.map((source, index) => (
          <CRow key={index}>
            <CCol xs={11}>
              <CFormInput
                style={{ border: errorFields.includes(index) ? "2px solid red" : "" }}
                type="text"
                defaultValue={source?.address}
                size="sm"
                floatingLabel={t("SharedMailboxAddress")}
                // disabled={!configuration?.enableSharedMailboxSearch}
                onKeyUp={(e) => e.key === 'Enter' && handleSharedMailbox(e, index)}
                onBlur={(e) => handleSharedMailbox(e, index)}
              />
            </CCol>
            <CCol xs={1}>
              {configuration?.enableSharedMailboxSearch && <CIcon
                onClick={() => {
                  if (easyDirectorySource?.length > 1) {
                    const newSource = [...easyDirectorySource];
                    newSource.splice(index, 1);
                    setEasyDirectorySource(newSource);
                    const config = { ...configuration, sharedMailboxes: newSource.slice(0, -1) };
                    setConfiguration(config);
                  }
                }}
                icon={cilTrash}
                size="xxl"
                style={{ 'color': 'red', marginTop: "12px", cursor: 'pointer', display: easyDirectorySource?.length > 1 && index !== easyDirectorySource?.length - 1 ? 'inline' : 'none' }}
              />}
            </CCol>
          </CRow>
        ))}

      </div>
    );
  };

  const RenderFeatureManager = (props: { configuration, setConfiguration, easyDirectorySource, setEasyDirectorySource }) => {
    const { configuration, setConfiguration, easyDirectorySource, setEasyDirectorySource } = props;
    return (
      <CCard className="p-8">
        <CCardBody>
          <CCardTitle>{t("EasyDirectory.FeaturesTitle")}</CCardTitle>
          <CRow className="mb-3">
            <CCol sm={8}>
              <CCardText>
                {t("EasyDirectory.FeaturesDescription")}
              </CCardText>
              <CFormSwitch label={t("EasyDirectory.PersonalOutlookContacts")} id="enablePersonalMailboxSearch" defaultChecked={configuration?.enablePersonalMailboxSearch} onClick={(e) => updateConfiguration({ enablePersonalMailboxSearch: (e.target as HTMLInputElement).checked } as Partial<EasyDirectoryConfiguration>)} />
              <CFormSwitch label={t("EasyDirectory.EntraIdContacts")} id="enableEntraIdSearch" defaultChecked={configuration?.enableEntraIdSearch} onClick={(e) => updateConfiguration({ enableEntraIdSearch: (e.target as HTMLInputElement).checked } as Partial<EasyDirectoryConfiguration>)} />
              {/* <CFormSwitch label={t("EasyDirectory.ThirdPartyContacts")} id="enableSharedMailboxSearch" defaultChecked={configuration?.enableSharedMailboxSearch} onClick={(e) => updateConfiguration({ enableSharedMailboxSearch: (e.target as HTMLInputElement).checked } as Partial<EasyDirectoryConfiguration>)} /> */}
              <br />
              <br />
              <h5>{t("EasyDirectory.ThirdPartyContacts")}</h5>
              <CAlert color="warning">
                <svg style={{ marginTop: "-5px" }} className="flex-shrink-0 me-2" width="24" height="24" viewBox="0 0 512 512">
                  <rect
                    width="32"
                    height="176"
                    x="240"
                    y="176"
                    fill="var(--ci-primary-color, currentColor)"
                    className="ci-primary"
                  ></rect>
                  <rect
                    width="32"
                    height="32"
                    x="240"
                    y="384"
                    fill="var(--ci-primary-color, currentColor)"
                    className="ci-primary"
                  ></rect>
                  <path
                    fill="var(--ci-primary-color, currentColor)"
                    d="M274.014,16H237.986L16,445.174V496H496V445.174ZM464,464H48V452.959L256,50.826,464,452.959Z"
                    className="ci-primary"
                  ></path>
                </svg>
                {t("EasyDirectory.ThirdPartyContactsInfo")}
              </CAlert>
              <RenderEmailValidation
                configuration={configuration}
                setConfiguration={setConfiguration}
                easyDirectorySource={easyDirectorySource}
                setEasyDirectorySource={setEasyDirectorySource}
                setEnableSaveSettings={setEnableSaveSettings}
              />
            </CCol>
            <CCol sm={4}>
            </CCol>
          </CRow>
          <CRow className="tcf-flexbox tcf-align-end">
            <CCol xs={2} className="tcf-flexbox tcf-align-end">
              <CButton
                disabled={!enableSaveSettings || isLoading}
                className="mr-1 mb-1"
                style={{ backgroundColor: (!enableSaveSettings || isLoading) && "Gray", borderColor: (!enableSaveSettings || isLoading) && "Gray" }}
                color="primary"
                onClick={() => openModal(saveSettings)}
              >
                {isLoading ? (
                  <CSpinner size="sm" component="span" aria-hidden="true" />
                ) : (
                  t("Button.SaveSettings")
                )}
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    );
  };

  const RenderTagsManager = (props: { configuration: EasyDirectoryConfiguration, setConfiguration: (config: EasyDirectoryConfiguration) => void}) => {
    const { configuration } = props;

    const handleAvailableTag = (tag: EasyTag, mode: string) => {
      const newConfig = { ...configuration };
      if (mode === "add" && !newConfig.orgTags?.some(t => t.label === tag.label)) {
        newConfig.orgTags = [...(newConfig.orgTags || []), tag];
      } else if (mode === "delete") {
        newConfig.orgTags = newConfig.orgTags?.filter(removaltag => removaltag?.id !== tag?.id);
      }
      setConfiguration(newConfig);
    };
    return (
      <CCard>
        <CCardBody>
          <CCardTitle>{t("EasyDirectory.ManageTags")}</CCardTitle>
          <CRow>
            <CCol xs={12}>
              <CFormInput
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAvailableTag({ id: uuid(), label: (e.target as HTMLInputElement).value }, "add");
                    (e.target as HTMLInputElement).value = "";
                  }
                }}
                text={t("AvailableTagsDescription")}
              />
              <div className="tcf-available-tags">
                {configuration?.orgTags?.map((tag) => {
                  return <><CButton key={tag?.id} color="light" size="sm" onClick={() => handleAvailableTag(tag, "delete")}>{tag?.label} <CIcon icon={cilX} /> </CButton>&nbsp;</>
                })}
              </div>
            </CCol>
          </CRow>
          <CRow className="tcf-flexbox tcf-align-end">
            <CCol xs={2} className="tcf-flexbox tcf-align-end">
              <CButton disabled={!enableSaveTags} className="mr-1 mb-1" style={{
                backgroundColor: !enableSaveTags && "Gray",
                borderColor: !enableSaveTags && "Gray"
              }} color="primary" onClick={() => openModal(saveTags)}>
                {t("Button.SaveTags")}
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard >
    );
  };

  const RenderViewManager = (props: { configuration: EasyDirectoryConfiguration, selectedView: View, setSelectedView: (view: View) => void, setConfiguration: (config: EasyDirectoryConfiguration) => void }) => {
    const { configuration, selectedView, setSelectedView, setConfiguration } = props;

    const findViewById: (data: ViewSelect[], viewId: string) => View = (data: ViewSelect[], viewId: string) => data.flatMap(user => user.views)!.find(view => view?.id === viewId);

    const handleAvailableView = async (view: View, mode: string) => {
      const newConfig = { ...configuration };
      if (mode === "add") {
        if (newConfig.orgViews?.some(item => item?.id === view?.id)) {
          alert(t("EasyDirectory.ViewAlreadyAdded"));
          return;
        }
        newConfig.orgViews = [...(newConfig.orgViews || []), view];

        // Remove the added view from viewSelectionList
        setViewSelectionList(prevList =>
          prevList.map(group => ({
            ...group,
            options: group.options.filter(option => option.value !== view.id)
          })).filter(group => group.options.length > 0)
        );
      } else if (mode === "delete") {
        newConfig.orgViews = newConfig.orgViews?.filter(removalView => removalView?.id !== view?.id);

        // Add the removed view back to viewSelectionList
        const userView = customViews?.find(userView => userView.views.some(v => v.id === view.id));
        if (userView) {
          graphService.getUser(userView.userId).then(person => {
            setViewSelectionList(prevList => {
              const existingGroup = prevList.find(group => group.label === person.displayName);
              if (existingGroup) {
                return prevList.map(group =>
                  group.label === person.displayName
                    ? { ...group, options: [...group.options, { value: view.id, label: view.label }] }
                    : group
                );
              } else {
                return [...prevList, {
                  label: person.displayName,
                  options: [{ value: view.id, label: view.label }]
                }];
              }
            });
          });
        }
      }
      setConfiguration(newConfig);
    };

    const OnSelectedView = (newValue, actionMeta) => {
      switch (actionMeta.action) {
        case 'select-option':
          setSelectedView(findViewById(customViews, newValue.value));
          break;
        case 'remove-value':
        case 'pop-value':
          setSelectedView(undefined);
          break;
      }
    };

    const userOptions = useMemo(() =>
      viewSelectionList.map(group => ({
        value: group.label,
        label: group.label
      })),
      [viewSelectionList]
    );

    const renderJSON = (jsonString: string) => {
      try {
        const parsedJSON = JSON.parse(jsonString);
        return (
          <pre style={{
            backgroundColor: '#f4f4f4',
            padding: '10px',
            borderRadius: '5px',
            overflow: 'auto',
            maxHeight: '200px'
          }}>
            {JSON.stringify(parsedJSON, null, 2)}
          </pre>
        );
      } catch (error) {
        return <span>Invalid JSON</span>;
      }
    };

    const renderPageOptions = (pageOptions) => {
      if (!pageOptions) return null;
      return (
        <div>
          {pageOptions.presenceView !== undefined && (
            <p><strong>Presence View:</strong> {pageOptions.presenceView ? "✅" : "❌"}</p>
          )}
          {pageOptions.fullScreen !== undefined && (
            <p><strong>Full Screen:</strong> {pageOptions.fullScreen ? "✅" : "❌"}</p>
          )}
          {pageOptions.enableTagging !== undefined && (
            <p><strong>Enable Tagging:</strong> {pageOptions.enableTagging ? "✅" : "❌"}</p>
          )}
          {pageOptions.groupedBy && (
            <div>
              <strong>Grouped By:</strong>
              {renderJSON(JSON.stringify(pageOptions.groupedBy))}
            </div>
          )}
        </div>
      );
    };

    const handleUserChange = (selectedOption) => {
      setSelectedUser(selectedOption);
      setSelectedView(null);
    };

    const filteredViewSelectionList = selectedUser
      ? viewSelectionList.filter(group => group.label === selectedUser.label)
      : viewSelectionList;

    return (
      <CCard>
        <CCardBody>
          <CCardTitle>{t("EasyDirectory.ManageOrganizationViews")}</CCardTitle>
          <CRow className="mb-3">
            {/* Left Column for Select and Badges */}
            <CCol xs={6} className="d-flex flex-column">
              <Select
                options={userOptions}
                onChange={handleUserChange}
                value={selectedUser}
                isClearable
                placeholder={t("Select.FilterByUser")}
                className="mb-3  me-2"
              />
              <div className="d-flex align-items-center mb-3">
                <Select
                  options={filteredViewSelectionList}
                  onChange={OnSelectedView}
                  className="tcf-custom-view-select me-2"
                  isSearchable
                  isClearable
                />
                <CButton disabled={selectedView === null} color="dark" onClick={() => handleAvailableView(selectedView, "add")}>{t("Add")}</CButton>
              </div>
              <CCard className="mt-3">
                <CCardBody>
                  {selectedView !== null ? <>
                    <CCardTitle>{selectedView?.label}</CCardTitle>
                    <div>
                      {selectedView?.id && <p><strong>ID:</strong> {selectedView?.id}</p>}
                      {selectedView?.searchTemplate && (
                        <div>
                          <strong>Search Template:</strong>
                          {renderJSON(selectedView.searchTemplate)}
                        </div>
                      )}
                      {renderPageOptions(selectedView?.pageOptions)}
                    </div>
                  </> : <CCardTitle style={{ textAlign: "center", color: "gray" }}><span>{t("Preview")}</span></CCardTitle>}
                </CCardBody>
              </CCard>
            </CCol>
            <CCol xs={6}>
              <div className="tcf-org-views">
                {configuration?.orgViews?.map((views) => (
                  <CButton style={{ marginRight: "5px" }} className="mr-1 mb-1" key={views?.id} color="light" size="sm" onClick={() => handleAvailableView(views, "delete")}>
                    {views?.label} <CIcon icon={cilX} />
                  </CButton>
                ))}
              </div>
            </CCol>
          </CRow>

          <CRow className="tcf-flexbox tcf-align-end">
            <CCol xs={2} className="tcf-flexbox tcf-align-end">
              <CButton disabled={!enableSaveViews} style={{
                backgroundColor: !enableSaveViews && "Gray",
                borderColor: !enableSaveViews && "Gray"
              }} color="primary" onClick={() => openModal(saveView)}>
                {t("Button.SaveViews")}
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    );
  };

  const RenderSaveChangesModal = () => {
    return (<ConfirmationModal visible={modalVisible}
      toggleModal={toggleModal}
      onConfirm={onConfirm} />);
  };

  return (easyContext.productLicenses?.EasyDirectory?.isLicensed && (
    <div>
      <br />
      {isPageConfigLoading ?
        <div className="d-flex justify-content-center tcf-loading-background">
          <CSpinner variant="grow" />&nbsp;&nbsp;&nbsp;<h5>{loadingMsg}</h5>
        </div> : <></>
      }

      <CToaster className="p-3" placement="top-end" push={toast} ref={toaster} />
      <CImage fluid src={EasyDirectoryLogo} alt="Easy Directory Configuration" width={370} /><br /><br />
      <p>
        {t("EasyDirectory.Introduction")}
      </p>
      <CRow>
        <CCol xs={6} >
          <CCard className="tcf-panel-card">
            <CCardBody>
              <CRow>
                <CCol>
                  <ReactPlayer url='https://www.youtube.com/watch?v=mcSgNQXaWQw' pip={true} playsinline={true} controls={false} width='100%' />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol>
          <CCard className="tcf-panel-card">
            <CCardImage orientation="top" src={LicenseImg} className="tcf-card-img" />
            <CCardBody>
              <CCardTitle>{seatsCount + " / " + easyContext.productLicenses?.EasyDirectory?.seatCount} {t("EasyDirectory.LicensesTitle")}</CCardTitle>
              <CCardText>
                {t("EasyDirectory.LicensesDescription")}<br /><br />

              </CCardText>
              <CButton
                className="mr-1 mb-1"
                color="dark"
                href="/subscriptions"
              >
                <CIcon icon={cilGroup} />
                &nbsp;&nbsp;{t("Button.ReserveSeats")}
              </CButton>
              <CButton className="mr-1 mb-1" href="/subscriptions" color="dark">
                <CIcon icon={cilLoopCircular} />
                &nbsp;&nbsp;{t("EasyDirectory.LicensesButton")}
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol>
          <CCard className="tcf-panel-card">
            <CCardImage orientation="top" src={SupportImg} className="tcf-card-img" />
            <CCardBody>
              <CCardTitle>{t("SupportTitle")}</CCardTitle>
              <CCardText>
                {t("SupportDescription")}<br /><br />
                <CLink href="https://docs.easydirectory.easyplatform.app/" target="_blank">Easy Directory Configuration Guide</CLink><br /><br />
              </CCardText>
              <CButton href="mailto:support@twincapfirst.ch?subject=Easy%20Call%20Report" target="_blank" color="dark">
                <CIcon icon={cilPhone} />
                &nbsp;&nbsp;{t("SupportButton")}
              </CButton>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <br />
      <RenderFeatureManager
        configuration={configuration}
        setConfiguration={setConfiguration}
        easyDirectorySource={easyDirectorySource}
        setEasyDirectorySource={setEasyDirectorySource} />
      <br />
      <RenderTagsManager configuration={configuration} setConfiguration={setConfiguration} />
      <br />
      <RenderRoleManager
        configuration={configuration}
        setConfiguration={setConfiguration}
        setSuccessMsg={setSuccessMsg}
        addToast={addToast}
        setErrorMsg={setErrorMsg}
        errorToast={errorToast}
        successfulToast={successfulToast} />
      <br />
      <RenderViewManager configuration={configuration} selectedView={selectedView} setConfiguration={setConfiguration} setSelectedView={setSelectedView} />
      <br />
      <RenderSaveChangesModal />
    </div>)
  )
}

export default EasyDirectory